/**
 * NECS Admin → Leads CRM (Inbound Lead Capture Unified View).
 *
 * Displays every inbound lead from RingCentral (Phone), Website widget,
 * and In-App widget in a single unified table. Supports filters (source,
 * service, status, date range), inline status updates, and CSV export
 * respecting the active filters.
 *
 * Corresponds to backend routes in /app/backend/routes/leads.py:
 *   GET    /api/admin/leads-inbound
 *   PATCH  /api/admin/leads-inbound/{lead_id}/status
 *   GET    /api/admin/leads-inbound/export.csv
 */
import { useEffect, useState, useMemo } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Download, RefreshCw, Phone, Globe, Smartphone, Filter, Flame, Radio } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LIST = ['New', 'Contacted', 'Quoted', 'Converted', 'Closed'];
const STATUS_COLORS = {
  New:       'bg-blue-500 text-white',
  Contacted: 'bg-amber-500 text-black',
  Quoted:    'bg-purple-500 text-white',
  Converted: 'bg-emerald-500 text-white',
  Closed:    'bg-zinc-500 text-white',
};

const SOURCES = [
  { value: 'all',         label: 'All Sources' },
  { value: 'ringcentral', label: 'Phone (RingCentral)' },
  { value: 'website',     label: 'Website' },
  { value: 'app',         label: 'In-App' },
];

const SOURCE_META = {
  ringcentral: { label: 'Phone',   icon: Phone,      color: '#3b82f6' },
  website:     { label: 'Website', icon: Globe,      color: '#10b981' },
  app:         { label: 'App',     icon: Smartphone, color: '#a855f7' },
};

// Same catalog the widget uses, kept aligned so the service filter can
// list every option even when the current result set is empty.
const SERVICE_OPTIONS = [
  'Waste Management', 'Recycling', 'Janitorial Cleaning', 'Sanitation',
  'Floor Care', 'Floor Polishing', 'Floor Restoration', 'Window Cleaning',
  'Pressure Washing', 'Bulk Removal', 'Day Porter Services',
  'Property Appearance', 'Dumpster & Trash Room Care', 'Deep Cleaning',
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function LeadsInboundCRMTab() {
  const [leads, setLeads] = useState([]);
  const [countsByStatus, setCountsByStatus] = useState({});
  const [countsBySource, setCountsBySource] = useState({});
  const [loading, setLoading] = useState(false);
  const [sortByPriority, setSortByPriority] = useState(true);
  // Master Addendum · On-Call rotation state.
  const [salesReps, setSalesReps] = useState([]);
  const [currentOnCall, setCurrentOnCall] = useState(null);
  const [filters, setFilters] = useState({
    source: 'all',
    service: 'all',
    status: 'all',
    date_from: '',
    date_to: '',
  });

  const buildQuery = () => {
    const p = new URLSearchParams();
    if (filters.source && filters.source !== 'all') p.set('source', filters.source);
    if (filters.service && filters.service !== 'all') p.set('service', filters.service);
    if (filters.status && filters.status !== 'all') p.set('status', filters.status);
    if (filters.date_from) p.set('date_from', filters.date_from);
    if (filters.date_to) p.set('date_to', filters.date_to);
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/leads-inbound?${buildQuery().toString()}`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to load leads');
        return;
      }
      setLeads(data.leads || []);
      setCountsByStatus(data.counts_by_status || {});
      setCountsBySource(data.counts_by_source || {});
    } catch (err) {
      toast.error('Network error');
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters]);

  // Master Addendum · Load sales reps for the On-Call toggle strip.
  const loadSalesReps = async () => {
    try {
      const res = await fetch(`${API}/api/admin/sales-reps`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setSalesReps(data.reps || []);
      setCurrentOnCall(data.current_on_call || null);
    } catch { /* silent */ }
  };
  useEffect(() => { loadSalesReps(); }, []);

  const toggleOnCall = async (userId, next) => {
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/on-call`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ on_call: next }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to update on-call');
        return;
      }
      toast.success(next ? 'On-call rep updated' : 'On-call cleared');
      await loadSalesReps();
    } catch { toast.error('Network error'); }
  };

  const displayedLeads = useMemo(() => {
    const rows = [...leads];
    if (sortByPriority) {
      rows.sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
    }
    return rows;
  }, [leads, sortByPriority]);

  const updateStatus = async (leadId, status) => {
    try {
      const res = await fetch(`${API}/api/admin/leads-inbound/${leadId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to update status');
        return;
      }
      toast.success(`Lead marked ${status}`);
      // optimistic
      setLeads((rows) => rows.map((r) => (r.lead_id === leadId ? { ...r, status } : r)));
    } catch { toast.error('Network error'); }
  };

  const exportCsv = async () => {
    try {
      const url = `${API}/api/admin/leads-inbound/export.csv?${buildQuery().toString()}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Export failed');
        return;
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `necs_inbound_leads_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('CSV exported');
    } catch { toast.error('Export failed'); }
  };

  const totalRow = useMemo(() => leads.length, [leads]);

  return (
    <div className="space-y-4" data-testid="leads-inbound-crm">
      {/* Header stats */}
      <Card className="p-4" style={{ backgroundColor: '#1F1F1F', borderColor: '#3D3D3D' }}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-white uppercase tracking-wide"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Leads CRM · Noble AI Unified Capture
            </h3>
            <p className="text-xs text-zinc-400 mt-1">
              Every inbound lead from RingCentral, the marketing site widget, and the in-app widget lands here.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm" onClick={load} disabled={loading}
              data-testid="leads-refresh-btn"
              className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
            >
              <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              size="sm" onClick={exportCsv}
              data-testid="leads-export-csv-btn"
              className="bg-[#FFD60A] text-black hover:bg-yellow-300 font-bold"
            >
              <Download size={14} className="mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Source / status count strip */}
        <div className="flex flex-wrap gap-2 text-xs">
          {SOURCES.filter((s) => s.value !== 'all').map((s) => {
            const Icon = SOURCE_META[s.value]?.icon;
            const c = countsBySource[s.value] || 0;
            return (
              <div key={s.value}
                   className="rounded-md px-2.5 py-1 flex items-center gap-1.5"
                   style={{ backgroundColor: '#2D2D2D', border: '1px solid #3D3D3D' }}>
                {Icon && <Icon size={12} style={{ color: SOURCE_META[s.value].color }} />}
                <span className="text-zinc-300">{s.label}:</span>
                <span className="text-white font-bold">{c}</span>
              </div>
            );
          })}
          {STATUS_LIST.map((st) => (
            <div key={st} className="rounded-md px-2.5 py-1 flex items-center gap-1.5"
                 style={{ backgroundColor: '#2D2D2D', border: '1px solid #3D3D3D' }}>
              <Badge className={`${STATUS_COLORS[st]} text-[10px] px-1.5 py-0`}>{st}</Badge>
              <span className="text-white font-bold">{countsByStatus[st] || 0}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Master Addendum · On-Call rotation strip. One-click toggle so the
          admin can flip which sales rep receives the next inbound-lead SMS. */}
      <Card className="p-4" style={{ backgroundColor: '#1F1F1F', borderColor: '#3D3D3D' }}
            data-testid="on-call-rotation-card">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Radio size={14} style={{ color: '#FFD60A' }} />
            <h4 className="text-sm font-bold text-white uppercase tracking-wide"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              On-Call Sales Rep
            </h4>
          </div>
          {currentOnCall ? (
            <span className="text-xs text-zinc-300"
                  data-testid="on-call-current">
              Currently paged: <strong className="text-white">{currentOnCall.name || currentOnCall.email}</strong>
            </span>
          ) : (
            <span className="text-xs text-zinc-500 italic" data-testid="on-call-none">
              No rep on-call — SMS falls back to SALES_ONCALL_PHONE env var if set.
            </span>
          )}
        </div>
        {salesReps.length === 0 ? (
          <p className="text-xs text-zinc-500 italic" data-testid="on-call-empty">
            No sales users yet. Create a user with role `sales`, `sales_rep`, `sales_agent`, or `sales_manager` to enable rotation.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
               data-testid="on-call-rep-grid">
            {salesReps.map((rep) => {
              const active = !!rep.on_call;
              return (
                <button
                  key={rep.user_id}
                  type="button"
                  onClick={() => toggleOnCall(rep.user_id, !active)}
                  data-testid={`on-call-toggle-${rep.user_id}`}
                  className={`text-left rounded-lg border px-3 py-2 text-xs transition-colors ${
                    active
                      ? 'bg-[#FFD60A] text-black border-[#FFD60A]'
                      : 'bg-zinc-900 text-zinc-200 border-zinc-700 hover:border-zinc-500'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold truncate">{rep.name || rep.email}</span>
                    {active && <Radio size={12} className="animate-pulse" />}
                  </div>
                  <div className={`text-[10px] truncate mt-0.5 ${active ? 'text-black/70' : 'text-zinc-500'}`}>
                    {rep.role} · {rep.phone || 'no phone on file'}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {/* Filters */}
      <Card className="p-4" style={{ backgroundColor: '#1F1F1F', borderColor: '#3D3D3D' }}>
        <div className="flex items-center gap-2 mb-2 text-zinc-400 text-xs uppercase tracking-wider">
          <Filter size={12} /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <Select value={filters.source} onValueChange={(v) => setFilters({ ...filters, source: v })}>
            <SelectTrigger data-testid="filter-source" className="bg-zinc-900 border-zinc-700 text-white">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              {SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filters.service} onValueChange={(v) => setFilters({ ...filters, service: v })}>
            <SelectTrigger data-testid="filter-service" className="bg-zinc-900 border-zinc-700 text-white">
              <SelectValue placeholder="Service" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Services</SelectItem>
              {SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="filter-status" className="bg-zinc-900 border-zinc-700 text-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUS_LIST.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          <Input
            type="date" value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            data-testid="filter-date-from"
            className="bg-zinc-900 border-zinc-700 text-white"
          />
          <Input
            type="date" value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            data-testid="filter-date-to"
            className="bg-zinc-900 border-zinc-700 text-white"
          />
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-hidden" style={{ backgroundColor: '#1F1F1F', borderColor: '#3D3D3D' }}>
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">
            Leads
          </span>
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-300 cursor-pointer"
                 data-testid="sort-by-priority-toggle">
            <input type="checkbox" checked={sortByPriority}
                   onChange={(e) => setSortByPriority(e.target.checked)}
                   className="accent-[#FFD60A]" />
            Sort hottest first
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-inbound-table">
            <thead style={{ backgroundColor: '#0f0f0f' }}>
              <tr className="text-zinc-400 text-xs uppercase tracking-wider">
                <th className="px-3 py-2 text-left">Priority</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Caller</th>
                <th className="px-3 py-2 text-left">Property / Company</th>
                <th className="px-3 py-2 text-left">Services</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Follow-up</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-zinc-500 text-sm">
                    No leads match the current filters.
                  </td>
                </tr>
              )}
              {displayedLeads.map((row) => {
                const srcMeta = SOURCE_META[row.source] || { label: row.source, color: '#888', icon: Globe };
                const Icon = srcMeta.icon;
                const band = row.priority_band || 'cool';
                const bandColor = band === 'hot'  ? '#ef4444'
                                : band === 'warm' ? '#f59e0b'
                                : '#71717a';
                return (
                  <tr key={row.lead_id} className="border-t border-zinc-800 hover:bg-zinc-900/40"
                      data-testid={`lead-row-${row.lead_id}`}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 text-xs font-bold"
                            data-testid={`lead-priority-${row.lead_id}`}
                            style={{ color: bandColor }}>
                        <Flame size={12} />
                        <span className="uppercase tracking-wider">{band}</span>
                        <span className="text-zinc-500 font-normal ml-1">
                          ({row.priority_score ?? 0})
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-2 text-zinc-300 whitespace-nowrap">{fmtDate(row.created_at)}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium"
                            style={{ color: srcMeta.color }}>
                        <Icon size={12} /> {srcMeta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-white">
                      <div className="font-medium">{row.caller_name || '—'}</div>
                      {row.caller_phone && <div className="text-xs text-zinc-400">{row.caller_phone}</div>}
                    </td>
                    <td className="px-3 py-2 text-zinc-200">
                      <div>{row.company_or_property_name || '—'}</div>
                      {row.property_address && <div className="text-xs text-zinc-500">{row.property_address}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1 max-w-[240px]">
                        {(row.service_requested || []).map((s) => (
                          <Badge key={s} className="bg-zinc-800 text-zinc-200 border border-zinc-700 text-[10px]">
                            {s}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Select
                        value={row.status || 'New'}
                        onValueChange={(v) => updateStatus(row.lead_id, v)}
                      >
                        <SelectTrigger
                          data-testid={`status-select-${row.lead_id}`}
                          className={`h-7 w-[125px] text-xs border-0 ${STATUS_COLORS[row.status || 'New']}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_LIST.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-300">
                      {row.follow_up_email && <div>{row.follow_up_email}</div>}
                      {row.preferred_contact_method && (
                        <div className="text-zinc-500 capitalize">
                          via {row.preferred_contact_method}
                          {row.preferred_contact_time ? ` · ${row.preferred_contact_time}` : ''}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="border-t border-zinc-800 px-3 py-2 text-xs text-zinc-500">
          Showing <strong className="text-white">{totalRow}</strong> lead{totalRow === 1 ? '' : 's'}.
        </div>
      </Card>
    </div>
  );
}
